import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DataConnectionSchemaStatus, SchemaAnnotationsDelta } from "@/api/data-connections-api"

import { AlertCircle, CheckCircle2, FileText, Upload } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import {
  matchAgainstCatalog,
  type MatchReport,
  matchReportToDelta,
  parseAnnotationsText,
} from "./parse-annotations-text"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  catalog: DataConnectionSchemaStatus | null | undefined
  onApply: (delta: SchemaAnnotationsDelta) => Promise<void>
}

/**
 * Paste a full schema-documentation text block and preview what lands
 * where before committing. Uses the same parser that's unit-tested
 * against the SHL documentation format — see parse-annotations-text.ts.
 */
export function BulkImportAnnotationsDialog({ open, onOpenChange, catalog, onApply }: Props) {
  const { t } = useTranslation()
  const [text, setText] = useState("")
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const report: MatchReport | null = useMemo(() => {
    if (!text.trim()) return null
    const parsed = parseAnnotationsText(text)
    return matchAgainstCatalog(parsed, catalog)
  }, [text, catalog])

  const matchedCount = report?.matchedTables.length ?? 0
  const unmatchedCount = report?.unmatchedTables.length ?? 0
  const columnCount = useMemo(
    () => (report?.matchedTables ?? []).reduce((sum, m) => sum + m.matchedColumns.length, 0),
    [report]
  )

  const handleApply = async () => {
    if (!report) return
    setApplying(true)
    setError(null)
    try {
      const delta = matchReportToDelta(report)
      await onApply(delta)
      setText("")
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setApplying(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-full overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="size-5" />
            {t("adminDashboard.dataConnections.bulkImport.title", "Import schema documentation")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.dataConnections.bulkImport.description",
              "Paste a markdown/text document describing tables and columns. Everything before the first divider line becomes the connection summary; each block between dividers is parsed as one table with its columns."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="bulk-text">
              {t("adminDashboard.dataConnections.bulkImport.paste", "Paste text")}
            </label>
            <Textarea
              className="font-mono text-xs"
              id="bulk-text"
              placeholder={IMPORT_PLACEHOLDER}
              rows={22}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">
              {t("adminDashboard.dataConnections.bulkImport.preview", "Preview")}
            </div>
            {!report && (
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.dataConnections.bulkImport.empty",
                  "Paste text on the left to see what will be imported."
                )}
              </p>
            )}
            {report && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <SummaryPill
                    count={matchedCount}
                    tone="success"
                    label={t(
                      "adminDashboard.dataConnections.bulkImport.matchedTables",
                      "matched tables"
                    )}
                  />
                  <SummaryPill
                    count={columnCount}
                    tone="success"
                    label={t(
                      "adminDashboard.dataConnections.bulkImport.matchedColumns",
                      "matched columns"
                    )}
                  />
                  {unmatchedCount > 0 && (
                    <SummaryPill
                      count={unmatchedCount}
                      tone="warning"
                      label={t(
                        "adminDashboard.dataConnections.bulkImport.unmatchedTables",
                        "unmatched tables"
                      )}
                    />
                  )}
                  {report.summary && (
                    <SummaryPill
                      count={1}
                      label={t("adminDashboard.dataConnections.bulkImport.summarySet", "summary")}
                      tone="success"
                    />
                  )}
                </div>

                <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1 text-xs">
                  {report.matchedTables.map((m) => (
                    <div key={`${m.schema}.${m.table}`} className="rounded border p-2">
                      <div className="flex items-start gap-1">
                        <CheckCircle2 className="text-success mt-0.5 size-3 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono font-medium">
                            {m.schema}.{m.table}
                          </div>
                          {m.parsed.description && (
                            <div className="text-muted-foreground truncate">
                              {m.parsed.description}
                            </div>
                          )}
                          <div className="text-muted-foreground mt-1">
                            {m.matchedColumns.length > 0 && (
                              <span>
                                {m.matchedColumns.length}{" "}
                                {t("adminDashboard.dataConnections.bulkImport.cols", "cols")}
                              </span>
                            )}
                            {m.unmatchedColumns.length > 0 && (
                              <span className="text-warning-ink">
                                {" · "}
                                {m.unmatchedColumns.length}{" "}
                                {t(
                                  "adminDashboard.dataConnections.bulkImport.colsNotInCatalog",
                                  "not in catalog"
                                )}
                                : {m.unmatchedColumns.slice(0, 5).join(", ")}
                                {m.unmatchedColumns.length > 5 && "…"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {report.unmatchedTables.map((u) => (
                    <div
                      key={u.tableName}
                      className="border-warning-ink/40 bg-warning/30 rounded border p-2"
                    >
                      <div className="flex items-start gap-1">
                        <AlertCircle className="text-warning-ink mt-0.5 size-3 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono font-medium">{u.tableName}</div>
                          <div className="text-muted-foreground text-[11px]">
                            {t(
                              "adminDashboard.dataConnections.bulkImport.noCatalogMatch",
                              "Not in catalog — skipped"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <p className="text-destructive text-xs">{error}</p>}

        <DialogFooter>
          <Button
            disabled={applying}
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            disabled={!report || (matchedCount === 0 && !report.summary) || applying}
            type="button"
            onClick={() => void handleApply()}
          >
            {applying ? <Spinner className="mr-2 size-4" /> : <Upload className="mr-2 size-4" />}
            {t("adminDashboard.dataConnections.bulkImport.apply", "Apply to catalog")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SummaryPill({
  count,
  label,
  tone,
}: {
  count: number
  label: string
  tone: "success" | "warning"
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5",
        tone === "success"
          ? STATUS_TONES.success.soft
          : "border-warning-ink/40 bg-warning/30 text-warning-ink"
      )}
    >
      <span className="font-semibold tabular-nums">{count}</span>
      <span>{label}</span>
    </span>
  )
}

const IMPORT_PLACEHOLDER = `Free-form intro paragraphs go to the connection summary...

_____________________________________________________________

club_dimension
Tabell
•    club_dimension: Dimensionstabell med grunddata om klubbar.
Kolumner
•    clubId [numeriskt]: Internt heltals ID.
•    clubName [text]: Visningsnamn.

_____________________________________________________________

purchase_fact
Tabell
•    purchase_fact: Faktatabell för köp.
Kolumner
•    totalAmount [numeriskt]: Totalbelopp per rad.`
